import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BeatLoader from "react-spinners/BeatLoader";
import { v4 } from "uuid";
import { BsStars } from "react-icons/bs";
import { FaPlus } from "react-icons/fa";
import HeaderComp from "./Components/HeaderComp";
import Footer from "./Components/Footer";
import CourseCard from "./Components/CourseCard";
import { UserAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { newJitsiRoom } from "../utils/courseTime";
import { sharePrice, formatPieces, splitPayout } from "../utils/pricing";

const EMPTY_FORM = {
  name: "",
  subject: "",
  date: "",
  time: "",
  duration: 60,
  price: 10,
  description: "",
  maxStudents: 5,
  videoType: "jitsi", // "jitsi" = salle créée automatiquement, "custom" = lien du prof
  meetLink: "",
};

const nowTimeHM = () => new Date().toTimeString().slice(0, 5);

const todayISO = () => new Date().toISOString().slice(0, 10);

// Renvoie un message d'erreur, ou "" si le formulaire est valide
const validate = (f) => {
  if (f.name.trim().length < 4) return "Le nom du cours doit faire au moins 4 caractères.";
  if (f.subject.trim().length < 3) return "Le sujet doit faire au moins 3 caractères.";
  if (f.description.trim().length < 10) return "La description doit faire au moins 10 caractères.";
  if (!f.date) return "Choisissez une date.";
  if (f.date < todayISO()) return "La date doit être aujourd'hui ou dans le futur.";
  if (!f.time) return "Choisissez une heure de début.";
  if (f.date === todayISO() && f.time <= nowTimeHM()) return "Cette heure est déjà passée.";
  if (!(Number(f.price) >= 1)) return "Le prix doit être d'au moins 1 pièce.";
  if (!(Number.isInteger(Number(f.maxStudents)) && Number(f.maxStudents) >= 1)) {
    return "Le nombre d'élèves doit être un entier ≥ 1.";
  }
  if (f.videoType === "custom") {
    if (!f.meetLink.trim()) return "Collez le lien de votre réunion (Meet, Zoom…).";
    if (!/^https:\/\/\S+$/i.test(f.meetLink.trim())) {
      return "Le lien doit commencer par https:// et ne pas contenir d'espace.";
    }
  }
  return "";
};

const inputClass =
  "w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-prune";

const MesCours = () => {
  const {
    user,
    currentMode,
    getAllCourses,
    getAllProfs,
    getAllCoursStudentByStudentMail,
    createCourseDocument,
    getCourseSession,
    deleteCourse,
    claimCoursePayout,
  } = UserAuth();
  const { t, lang } = useLang();

  const [sessions, setSessions] = useState({}); // courseId → infos salle vidéo

  const [myCourses, setMyCourses] = useState([]); // cours créés (prof)
  const [joinedCourses, setJoinedCourses] = useState([]); // cours rejoints
  const [professeurs, setProfesseurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [deleteConfirmMap, setDeleteConfirmMap] = useState({});
  const [deleting, setDeleting] = useState(false);

  const isTeacher = currentMode === "prof";

  const load = useCallback(async () => {
    if (!user) return;
    setError("");
    try {
      const [allCourses, allProfs, enrollments] = await Promise.all([
        getAllCourses(),
        getAllProfs(),
        getAllCoursStudentByStudentMail(user.email),
      ]);
      setProfesseurs(allProfs);
      const byDate = (a, b) =>
        `${a.date || ""}${a.time || ""}`.localeCompare(`${b.date || ""}${b.time || ""}`);
      const mine = allCourses.filter((c) => c.ownerId === user.uid).sort(byDate);
      const joinedIds = new Set(enrollments.map((e) => e.coursId));
      const joined = allCourses.filter((c) => joinedIds.has(c.id)).sort(byDate);
      setMyCourses(mine);
      setJoinedCourses(joined);

      // Salles vidéo : uniquement pour les cours du prof et ceux rejoints
      const entries = await Promise.all(
        [...mine, ...joined].map(async (c) => [c.id, await getCourseSession(c)])
      );
      setSessions(Object.fromEntries(entries));
    } catch (e) {
      console.error(e);
      setError(t("Impossible de charger vos cours."));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, user?.email]);

  useEffect(() => {
    load();
  }, [load]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormError("");
  };

  const closeForm = () => {
    setShowForm(false);
    setFormData(EMPTY_FORM);
    setFormError("");
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const msg = validate(formData);
    if (msg) {
      setFormError(t(msg));
      return;
    }
    setSaving(true);
    try {
      const id = v4();
      await createCourseDocument(
        {
          id,
          name: formData.name.trim(),
          subject: formData.subject.trim(),
          ownerId: user.uid,
          date: formData.date,
          time: formData.time,
          duration: Number(formData.duration),
          videoType: formData.videoType,
          jitsiRoom: formData.videoType === "jitsi" ? newJitsiRoom(v4()) : "",
          price: Number(formData.price),
          maxStudents: Number(formData.maxStudents),
          description: formData.description.trim(),
          meetLink: formData.videoType === "custom" ? formData.meetLink.trim() : "",
        });
      closeForm();
      await load();
    } catch (err) {
      console.error(err);
      setFormError(t("La création du cours a échoué. Vérifiez votre connexion et réessayez."));
    } finally {
      setSaving(false);
    }
  };

  const toggleDeleteConfirm = (courseId) => {
    setDeleteConfirmMap((prev) => ({ ...prev, [courseId]: !prev[courseId] }));
  };

  const handleDelete = async (courseId) => {
    setDeleting(true);
    try {
      await deleteCourse(courseId); // rembourse les élèves puis supprime
      await load();
    } catch (err) {
      console.error(err);
      setError(t("La suppression du cours a échoué."));
    } finally {
      setDeleting(false);
    }
  };

  const formValid = !validate(formData);

  return (
    <div className="min-h-screen bg-creme flex flex-col">
      <HeaderComp />

      <div className="bg-prune text-white py-10 px-6 text-center">
        <div className="flex items-center justify-center gap-2">
          <BsStars className="text-or" />
          <h1 className="text-4xl md:text-5xl" style={{ fontFamily: "Alegreya, Georgia, serif", fontWeight: 700 }}>
            {isTeacher ? t("Mes cours (prof)") : t("Mes cours")}
          </h1>
          <BsStars className="text-or" />
        </div>
      </div>

      <div className="max-w-6xl mx-auto w-full px-4 py-10 flex-1 space-y-12">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
            ⚠️ {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <BeatLoader color="#502887" />
          </div>
        ) : (
          <>
            {/* ─── Cours créés (professeurs) ─── */}
            {isTeacher && (
              <section>
                <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                  <h2 className="text-2xl font-bold text-stone-800">{t("Les cours que je donne")}</h2>
                  <button
                    onClick={() => setShowForm(true)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-or text-prune-deep rounded-full font-semibold hover:bg-or-dark transition-colors"
                  >
                    <FaPlus size={12} /> {t("Créer un cours")}
                  </button>
                </div>
                {myCourses.length === 0 ? (
                  <p className="text-stone-500 bg-white rounded-2xl p-8 text-center shadow-sm">
                    {t("Vous n'avez encore créé aucun cours.")}
                  </p>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {myCourses.map((course) => (
                      <CourseCard
                        key={course.id}
                        course={course}
                        professeurs={professeurs}
                        deleteConfirm={deleteConfirmMap[course.id]}
                        toggleDeleteConfirm={toggleDeleteConfirm}
                        deleteCourse={handleDelete}
                        canDelete
                        isOwner
                        onClaim={async (c) => {
                          const r = await claimCoursePayout(c);
                          await load();
                          return r;
                        }}
                        user={user}
                        session={sessions[course.id]}
                      />
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* ─── Cours rejoints (mode élève) ─── */}
            {!isTeacher && (
            <section>
              <h2 className="text-2xl font-bold text-stone-800 mb-6">{t("Les cours que j'ai rejoints")}</h2>
              {joinedCourses.length === 0 ? (
                <div className="text-stone-500 bg-white rounded-2xl p-8 text-center shadow-sm">
                  <p>{t("Vous n'êtes inscrit·e à aucun cours pour l'instant.")}</p>
                  <Link to="/cours_a_venir" className="inline-block mt-4 text-prune font-semibold underline">
                    {t("Voir les cours à venir →")}
                  </Link>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {joinedCourses.map((course) => (
                    <CourseCard
                      key={course.id}
                      course={course}
                      professeurs={professeurs}
                      user={user}
                      session={sessions[course.id]}
                    />
                  ))}
                </div>
              )}
            </section>
            )}

            {isTeacher && joinedCourses.length > 0 && (
              <p className="text-sm text-stone-500 text-center">
                {t("Vous êtes aussi inscrit·e à {n} cours — passez en mode élève pour les voir.", { n: joinedCourses.length })}
              </p>
            )}
          </>
        )}
      </div>

      <Footer />

      {/* Overlay pendant la suppression */}
      {deleting && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
          <BeatLoader color="#FFFFFF" size={20} />
        </div>
      )}

      {/* ─── Formulaire de création ─── */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-start justify-center px-4 overflow-y-auto py-8">
          <form
            onSubmit={handleCreate}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 relative space-y-4 my-auto"
          >
            <button
              type="button"
              onClick={closeForm}
              className="absolute top-4 right-4 text-stone-400 hover:text-stone-700 text-2xl leading-none"
              aria-label={t("Fermer")}
            >
              ×
            </button>
            <h3 className="text-xl font-bold text-stone-800">{t("Nouveau cours")}</h3>

            <div>
              <label className="block text-xs font-semibold text-stone-600 mb-1">{t("Nom du cours")}</label>
              <input name="name" value={formData.name} onChange={handleInputChange} className={inputClass} placeholder={t("Ex. Fractions pour les nuls")} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-600 mb-1">{t("Sujet")}</label>
              <input name="subject" value={formData.subject} onChange={handleInputChange} className={inputClass} placeholder={t("Ex. Mathématiques")} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-600 mb-1">{t("Description")}</label>
              <textarea name="description" rows={3} value={formData.description} onChange={handleInputChange} className={`${inputClass} resize-none`} placeholder={t("Ce que les élèves vont apprendre...")} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1">{t("Date")}</label>
                <input type="date" name="date" min={todayISO()} value={formData.date} onChange={handleInputChange} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1">{t("Heure de début")}</label>
                <input type="time" name="time" value={formData.time} onChange={handleInputChange} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1">{t("Durée")}</label>
                <select name="duration" value={formData.duration} onChange={handleInputChange} className={`${inputClass} bg-white`}>
                  <option value={30}>30 min</option>
                  <option value={45}>45 min</option>
                  <option value={60}>{lang === "nl" ? "1 u" : "1 h"}</option>
                  <option value={90}>{lang === "nl" ? "1 u 30" : "1 h 30"}</option>
                  <option value={120}>{lang === "nl" ? "2 u" : "2 h"}</option>
                  <option value={180}>{lang === "nl" ? "3 u" : "3 h"}</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1">{t("Prix du cours (pièces)")}</label>
                <input type="number" min={1} name="price" value={formData.price} onChange={handleInputChange} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1">{t("Élèves max")}</label>
                <input type="number" min={1} step={1} name="maxStudents" value={formData.maxStudents} onChange={handleInputChange} className={inputClass} />
              </div>
            </div>

            {/* ─── Choix de la visio ─── */}
            <fieldset className="space-y-2">
              <legend className="block text-xs font-semibold text-stone-600 mb-1">{t("Visioconférence")}</legend>
              <label className={`flex items-start gap-3 border rounded-lg p-3 cursor-pointer ${formData.videoType === "jitsi" ? "border-prune bg-prune-light" : "border-stone-200"}`}>
                <input type="radio" name="videoType" value="jitsi" checked={formData.videoType === "jitsi"} onChange={handleInputChange} className="mt-1 accent-prune" />
                <span>
                  <span className="block text-sm font-semibold text-stone-800">{t("Salle Tatie Soso (Jitsi) — recommandé")}</span>
                  <span className="block text-xs text-stone-500">{t("Créée automatiquement, gratuite, sans limite de durée. Rien à installer.")}</span>
                </span>
              </label>
              <label className={`flex items-start gap-3 border rounded-lg p-3 cursor-pointer ${formData.videoType === "custom" ? "border-prune bg-prune-light" : "border-stone-200"}`}>
                <input type="radio" name="videoType" value="custom" checked={formData.videoType === "custom"} onChange={handleInputChange} className="mt-1 accent-prune" />
                <span>
                  <span className="block text-sm font-semibold text-stone-800">{t("Mon propre lien (Google Meet, Zoom…)")}</span>
                  <span className="block text-xs text-stone-500">{t("Attention : en version gratuite, Meet coupe à 60 min et Zoom à 40 min.")}</span>
                </span>
              </label>
              {formData.videoType === "custom" && (
                <input name="meetLink" value={formData.meetLink} onChange={handleInputChange} className={inputClass} placeholder={t("https://meet.google.com/... ou https://zoom.us/j/...")} />
              )}
            </fieldset>

            {formData.price && formData.maxStudents > 0 && (
              <p className="text-xs text-stone-500">
                {t("Prix partagé entre les inscrits : un élève seul paie {n}", { n: formatPieces(formData.price, lang) })}
                {Number(formData.maxStudents) > 1 && t(", à {m} chacun paie {n}", { m: formData.maxStudents, n: formatPieces(sharePrice(formData.price, formData.maxStudents), lang) })}.{" "}
                {t("Après le cours, vous recevez {n} ({total} − 10 % de commission Tatie Soso), quel que soit le nombre d'inscrits.", { n: formatPieces(splitPayout(formData.price).prof, lang), total: formatPieces(formData.price, lang) })}
              </p>
            )}

            {formError && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">⚠️ {formError}</div>
            )}

            <button
              type="submit"
              disabled={saving || !formValid}
              className="w-full py-3 bg-prune text-white rounded-full font-semibold hover:bg-prune-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? t("Création...") : t("Créer le cours")}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default MesCours;
